// services/dashboardService.js - UPDATED to work with index.php

const API_BASE_URL = 'https://nlngraclub.com/nlnapi/index.php';

class DashboardService {
    // Get authentication token
    static getToken() {
        return localStorage.getItem('nlngra_token');
    }

    // Helper for authenticated requests
    static async authenticatedRequest(url, method = 'POST', data = null) {
        const token = this.getToken();
        
        console.log('🔐 authenticatedRequest - URL:', url);
        console.log('🔐 authenticatedRequest - Method:', method);
        console.log('🔐 authenticatedRequest - Token exists:', !!token);
        
        if (!token) {
            console.error('❌ No token found in localStorage');
            throw new Error('Authentication required');
        }

        const options = {
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        };

        if (data && (method === 'POST' || method === 'PUT')) {
            options.body = JSON.stringify(data);
            console.log('🔐 Request body:', JSON.stringify(data, null, 2));
        }

        try {
            console.log('🚀 Sending request to:', url);
            const response = await fetch(url, options);
            console.log('📡 Response status:', response.status);
            
            // For GET requests that might return HTML on error
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('text/html')) {
                const text = await response.text();
                console.error('❌ Received HTML instead of JSON:', text.substring(0, 200));
                throw new Error('Server returned HTML instead of JSON. Please check server logs.');
            }
            
            if (!response.ok) {
                console.error('❌ HTTP error! status:', response.status);
                const errorText = await response.text();
                console.error('❌ Error response body:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('✅ Response parsed:', result);
            return result;
        } catch (error) {
            console.error('❌ API request error:', error);
            throw error;
        }
    }

    // ========== HELPER METHODS FOR MEMBERSHIP DISPLAY ==========
    
    // Get formatted membership type display (e.g., "Primary - Full Member" or "Dependent - Child Member")
    static getFormattedMembership(membershipType, membershipCategory, relationshipType = null) {
        const membershipTypeLabel = this.getMembershipTypeLabel(membershipType);
        let categoryLabel = this.getCategoryLabel(membershipCategory);
        
        // For dependents, show relationship type instead of generic category
        if (membershipType === 'dependent' && relationshipType) {
            categoryLabel = this.getRelationshipLabel(relationshipType);
        }
        
        if (categoryLabel && membershipTypeLabel) {
            return `${membershipTypeLabel} - ${categoryLabel}`;
        } else if (membershipTypeLabel) {
            return membershipTypeLabel;
        } else if (categoryLabel) {
            return categoryLabel;
        }
        return 'Member';
    }

    // Get membership type label
    static getMembershipTypeLabel(membershipType) {
        const types = {
            'primary': 'Primary',
            'dependent': 'Dependent'
        };
        return types[membershipType] || membershipType || 'Member';
    }

    // Get relationship label for dependents
    static getRelationshipLabel(relationshipType) {
        const relationships = {
            'child': 'Child Member',
            'spouse': 'Spouse Member',
            'parent': 'Parent Member',
            'sibling': 'Sibling Member',
            'other': 'Family Member'
        };
        return relationships[relationshipType] || 'Family Member';
    }

    // Get relationship prefix
    static getRelationshipPrefix(relationshipType) {
        const prefixes = {
            'child': 'CHL',
            'spouse': 'SPE',
            'parent': 'PAR',
            'sibling': 'SIB',
            'other': 'DEP'
        };
        return prefixes[relationshipType] || 'DEP';
    }

    // Get category label from value
    static getCategoryLabel(categoryValue) {
        const categories = {
            'full_member': 'Full Member',
            'junior_member': 'Junior Member',
            'family_member': 'Family Member',
            'honorary_member': 'Honorary Member',
            'associate_member': 'Associate Member',
            'non_bonny_staff': 'Non-Bonny Staff'
        };
        return categories[categoryValue] || categoryValue || '';
    }

    // Get category prefix
    static getCategoryPrefix(categoryValue) {
        const prefixes = {
            'full_member': 'FUL',
            'junior_member': 'CHL',  // Updated to CHL
            'family_member': 'VAR',   // Variable based on relationship
            'honorary_member': 'HON',
            'associate_member': 'ASC',
            'non_bonny_staff': 'NBS'
        };
        return prefixes[categoryValue] || '';
    }

    // Get full membership display with badge info
    static getMembershipDisplay(membershipType, membershipCategory, relationshipType = null) {
        const typeLabel = this.getMembershipTypeLabel(membershipType);
        let categoryLabel = this.getCategoryLabel(membershipCategory);
        let prefix = this.getCategoryPrefix(membershipCategory);
        
        // For dependents, override with relationship info
        if (membershipType === 'dependent' && relationshipType) {
            categoryLabel = this.getRelationshipLabel(relationshipType);
            prefix = this.getRelationshipPrefix(relationshipType);
        }
        
        return {
            fullDisplay: categoryLabel ? `${typeLabel} - ${categoryLabel}` : typeLabel,
            typeLabel: typeLabel,
            categoryLabel: categoryLabel,
            prefix: prefix,
            categoryValue: membershipCategory,
            relationshipType: relationshipType
        };
    }

    // Get all membership categories with details (static list)
    static getMembershipCategoriesList() {
        return [
            { 
                value: 'full_member', 
                label: 'Full Member', 
                prefix: 'FUL',
                description: 'NLNG, NSML, and RAIS Direct Staff and Secondees based in Bonny Island',
                note: 'Full access to all club facilities and events'
            },
            { 
                value: 'junior_member', 
                label: 'Junior Member', 
                prefix: 'CHL',
                description: 'Children aged 12-18 years whose parents are full members of The Club',
                note: 'Must be registered by a parent who is a full member. Receives CHL prefix.'
            },
            { 
                value: 'family_member', 
                label: 'Family Member', 
                prefix: 'VAR',
                description: 'Spouse, parents, or siblings of primary members',
                note: 'Select relationship type. Prefix varies (SPE/PAR/SIB/DEP)'
            },
            { 
                value: 'honorary_member', 
                label: 'Honorary Member', 
                prefix: 'HON',
                description: 'Renewable one-year memberships for deserving personalities',
                note: 'Limited to five per year. Requires RCEC approval'
            },
            { 
                value: 'associate_member', 
                label: 'Associate Member', 
                prefix: 'ASC',
                description: 'Employees of JV Oil/Gas Companies or major NLNG contractors on Bonny Island',
                note: 'Requires management application and verifiable employment evidence'
            },
            { 
                value: 'non_bonny_staff', 
                label: 'Non-Bonny Staff', 
                prefix: 'NBS',
                description: 'NLNG staff visiting from other locations',
                note: 'Cannot become members but may use facilities upon presenting a valid ID card'
            }
        ];
    }

    // Get membership category by value
    static getMembershipCategory(value) {
        const categories = this.getMembershipCategoriesList();
        return categories.find(cat => cat.value === value);
    }

    // ========== DASHBOARD API METHODS ==========

    // Get complete dashboard data - uses index.php?url=dashboard
    static async getDashboard(memberId) {
        try {
            const url = `${API_BASE_URL}?url=dashboard`;
            const data = { member_id: memberId };
            return await this.authenticatedRequest(url, 'POST', data);
        } catch (error) {
            console.error('Error fetching dashboard:', error);
            throw error;
        }
    }

    // Get current user info with family relationship
    static async getMyInfo() {
        try {
            const url = `${API_BASE_URL}?url=family/my-info`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching user info:', error);
            throw error;
        }
    }

    // Get family members (list all dependents)
    static async getFamilyMembers() {
        try {
            const url = `${API_BASE_URL}?url=family/list`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching family members:', error);
            throw error;
        }
    }

    // Add family member (dependent) - uses relationship field
    static async addFamilyMember(familyData) {
        console.log('📤 DashboardService.addFamilyMember called with:', familyData);
        try {
            const url = `${API_BASE_URL}?url=family/add`;
            console.log('📡 Request URL:', url);
            
            const token = this.getToken();
            console.log('🔑 Token present:', !!token);
            
            const payload = {
                first_name: familyData.first_name,
                last_name: familyData.last_name,
                email: familyData.email || '',
                phone: familyData.phone || '',
                relationship: familyData.relationship, // spouse, child, parent, sibling, other
                date_of_birth: familyData.date_of_birth || null,
                address: familyData.address || ''
            };
            
            const result = await this.authenticatedRequest(url, 'POST', payload);
            console.log('📥 Response from server:', result);
            return result;
        } catch (error) {
            console.error('❌ DashboardService.addFamilyMember error:', error);
            throw error;
        }
    }

    // Update family member (dependent)
    static async updateFamilyMember(familyData) {
        try {
            const url = `${API_BASE_URL}?url=family/update`;
            const payload = {
                dependent_id: familyData.dependent_id,
                first_name: familyData.first_name,
                last_name: familyData.last_name,
                relationship: familyData.relationship,
                phone: familyData.phone || '',
                date_of_birth: familyData.date_of_birth || null,
                address: familyData.address || ''
            };
            return await this.authenticatedRequest(url, 'POST', payload);
        } catch (error) {
            console.error('Error updating family member:', error);
            throw error;
        }
    }

    // Remove family member (dependent)
    static async removeFamilyMember(dependentId) {
        try {
            const url = `${API_BASE_URL}?url=family/remove`;
            return await this.authenticatedRequest(url, 'POST', { dependent_id: dependentId });
        } catch (error) {
            console.error('Error removing family member:', error);
            throw error;
        }
    }

    // Update profile
    static async updateProfile(profileData) {
        try {
            const url = `${API_BASE_URL}?url=profile`;
            const payload = {
                first_name: profileData.first_name,
                last_name: profileData.last_name,
                phone: profileData.phone,
                address: profileData.address,
                occupation: profileData.occupation,
                date_of_birth: profileData.date_of_birth,
                emergency_contact_name: profileData.emergency_contact_name,
                emergency_contact_phone: profileData.emergency_contact_phone,
                current_password: profileData.current_password,
                new_password: profileData.new_password
            };
            return await this.authenticatedRequest(url, 'POST', payload);
        } catch (error) {
            console.error('Error updating profile:', error);
            throw error;
        }
    }

    // Get events
    static async getEvents(limit = 10, upcoming = true) {
        try {
            const url = `${API_BASE_URL}?url=events&limit=${limit}&upcoming=${upcoming ? 'true' : 'false'}&_t=${Date.now()}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.getToken()}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching events:', error);
            throw error;
        }
    }

    // Register for event
    static async registerEvent(memberId, eventId, guestsCount = 0, specialRequirements = '') {
        try {
            const url = `${API_BASE_URL}?url=events/register`;
            const data = {
                member_id: memberId,
                event_id: eventId,
                guests_count: guestsCount,
                special_requirements: specialRequirements
            };
            return await this.authenticatedRequest(url, 'POST', data);
        } catch (error) {
            console.error('Error registering for event:', error);
            throw error;
        }
    }

    // Get activities
    static async getActivities(memberId, limit = 20) {
        try {
            const url = `${API_BASE_URL}?url=activities&limit=${limit}&_t=${Date.now()}`;
            const data = { member_id: memberId };
            return await this.authenticatedRequest(url, 'POST', data);
        } catch (error) {
            console.error('Error fetching activities:', error);
            throw error;
        }
    }

    // Get statistics
    static async getStats() {
        try {
            const url = `${API_BASE_URL}?url=stats&_t=${Date.now()}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching stats:', error);
            throw error;
        }
    }

    // Get announcements
    static async getAnnouncements(limit = 10) {
        try {
            const url = `${API_BASE_URL}?url=announcements&limit=${limit}&_t=${Date.now()}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching announcements:', error);
            throw error;
        }
    }

    // ========== MEMBERSHIP CATEGORY API METHODS ==========

    // Get all membership categories from API
    static async getMembershipCategories() {
        try {
            // Return static list since categories are defined in code
            return { 
                success: true, 
                categories: this.getMembershipCategoriesList() 
            };
        } catch (error) {
            console.error('Error fetching membership categories:', error);
            return { success: false, categories: this.getMembershipCategoriesList() };
        }
    }

    // Get members by category (admin only)
    static async getMembersByCategory(category) {
        try {
            const url = `${API_BASE_URL}?url=members-by-category`;
            return await this.authenticatedRequest(url, 'POST', { membership_category: category });
        } catch (error) {
            console.error('Error fetching members by category:', error);
            throw error;
        }
    }

    // Get category statistics (admin only)
    static async getCategoryStats() {
        try {
            const url = `${API_BASE_URL}?url=category-stats`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching category stats:', error);
            throw error;
        }
    }

    // Update member's category (admin only)
    static async updateMemberCategory(memberId, category) {
        try {
            const url = `${API_BASE_URL}?url=update-member-category`;
            return await this.authenticatedRequest(url, 'POST', { 
                member_id: memberId, 
                membership_category: category 
            });
        } catch (error) {
            console.error('Error updating member category:', error);
            throw error;
        }
    }

    // Get category distribution (admin only)
    static async getCategoryDistribution() {
        try {
            const url = `${API_BASE_URL}?url=category-distribution`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching category distribution:', error);
            throw error;
        }
    }

    // ========== ADMIN METHODS ==========

    // Get pending users (admin only)
    static async getPendingUsers() {
        try {
            const url = `${API_BASE_URL}?url=pending-users`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching pending users:', error);
            throw error;
        }
    }

    // Get active users (admin only)
    static async getActiveUsers() {
        try {
            const url = `${API_BASE_URL}?url=active-users`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching active users:', error);
            throw error;
        }
    }

    // Get suspended users (admin only)
    static async getSuspendedUsers() {
        try {
            const url = `${API_BASE_URL}?url=suspended-users`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching suspended users:', error);
            throw error;
        }
    }

    // Get all users (admin only)
    static async getAllUsers() {
        try {
            const url = `${API_BASE_URL}?url=all-users`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching all users:', error);
            throw error;
        }
    }

    // Approve user (admin only)
    static async approveUser(userId) {
        try {
            const url = `${API_BASE_URL}?url=approve-user`;
            return await this.authenticatedRequest(url, 'POST', { user_id: userId });
        } catch (error) {
            console.error('Error approving user:', error);
            throw error;
        }
    }

    // Disapprove user (admin only)
    static async disapproveUser(userId, reason) {
        try {
            const url = `${API_BASE_URL}?url=disapprove-user`;
            return await this.authenticatedRequest(url, 'POST', { user_id: userId, reason: reason });
        } catch (error) {
            console.error('Error disapproving user:', error);
            throw error;
        }
    }

    // Suspend user (admin only)
    static async suspendUser(userId, reason) {
        try {
            const url = `${API_BASE_URL}?url=suspend-user`;
            return await this.authenticatedRequest(url, 'POST', { user_id: userId, reason: reason });
        } catch (error) {
            console.error('Error suspending user:', error);
            throw error;
        }
    }

    // Activate user (admin only)
    static async activateUser(userId) {
        try {
            const url = `${API_BASE_URL}?url=activate-user`;
            return await this.authenticatedRequest(url, 'POST', { user_id: userId });
        } catch (error) {
            console.error('Error activating user:', error);
            throw error;
        }
    }

    // Delete user (admin only)
    static async deleteUser(userId) {
        try {
            const url = `${API_BASE_URL}?url=delete-user`;
            return await this.authenticatedRequest(url, 'POST', { user_id: userId });
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    }

    // Get user details (admin only)
    static async getUserDetails(userId) {
        try {
            const url = `${API_BASE_URL}?url=user-details`;
            return await this.authenticatedRequest(url, 'POST', { user_id: userId });
        } catch (error) {
            console.error('Error fetching user details:', error);
            throw error;
        }
    }

    // Update user (admin only)
    static async updateUser(userId, userData) {
        try {
            const url = `${API_BASE_URL}?url=update-user`;
            const payload = { user_id: userId, ...userData };
            return await this.authenticatedRequest(url, 'POST', payload);
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    }

    // Get admin dashboard stats (admin only)
    static async getAdminStats() {
        try {
            const url = `${API_BASE_URL}?url=admin-stats`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching admin stats:', error);
            throw error;
        }
    }

    // ========== SECTION METHODS ==========

    // Get sections
    static async getSections() {
        try {
            const url = `${API_BASE_URL}?url=sections&_t=${Date.now()}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.getToken()}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching sections:', error);
            throw error;
        }
    }

    // Join section
    static async joinSection(sectionId) {
        try {
            const url = `${API_BASE_URL}?url=sections/join`;
            return await this.authenticatedRequest(url, 'POST', { section_id: sectionId });
        } catch (error) {
            console.error('Error joining section:', error);
            throw error;
        }
    }

    // Get member sections
    static async getMemberSections() {
        try {
            const url = `${API_BASE_URL}?url=sections/my-sections`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching member sections:', error);
            throw error;
        }
    }

    // ========== BOOKING METHODS ==========

    // Get bookings
    static async getBookings() {
        try {
            const url = `${API_BASE_URL}?url=bookings`;
            return await this.authenticatedRequest(url, 'GET');
        } catch (error) {
            console.error('Error fetching bookings:', error);
            throw error;
        }
    }

    // Create booking
    static async createBooking(bookingData) {
        try {
            const url = `${API_BASE_URL}?url=bookings/create`;
            return await this.authenticatedRequest(url, 'POST', bookingData);
        } catch (error) {
            console.error('Error creating booking:', error);
            throw error;
        }
    }

    // Cancel booking
    static async cancelBooking(bookingId) {
        try {
            const url = `${API_BASE_URL}?url=bookings/cancel`;
            return await this.authenticatedRequest(url, 'POST', { booking_id: bookingId });
        } catch (error) {
            console.error('Error cancelling booking:', error);
            throw error;
        }
    }
}

export default DashboardService;